import {
  faFileCheck,
  faFolder,
  faFolderPlus,
  faSpinner,
  faTrash,
  faUpload,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  message,
  Progress,
  Table,
  TableColumnsType,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/base/PageHeader';
import {
  useCreateKnowledgeBaseFolderMutation,
  useDeleteKnowledgeBaseFileMutation,
  useKnowledgeBaseDirectoryQuery,
} from '../../graphql/schema';
import confirmModal from '../../helper/confirmModal';
import useFormModal from '../../helper/useFormModal';
import Page from '../../components/layout/Page';
import { UploadContentType } from '@app/shared';
import { RcFile } from 'antd/es/upload';

interface DataType {
  id: string;
  key: string;
  name: any;
  size?: number | null;
  type: 'folder' | 'file';
  url?: string | null;
  path?: string;
  actions?: any;
  processed?: boolean;
}
const allowedMimeTypes = Object.values(UploadContentType);
const maxUploadSizeMB = 10;
const KnowledgeBase = () => {
  const { id: knowledgeBaseId } = useParams() as { id: string };

  const [searchParams, setSearchParams] = useSearchParams();

  const path = searchParams.get('path') ?? undefined;

  const { data, loading, refetch, startPolling, stopPolling } =
    useKnowledgeBaseDirectoryQuery({
      variables: {
        knowledgeBaseId,
        path,
      },
    });

  useEffect(() => {
    if (data?.knowledgeBaseDirectory.files?.find((file) => !file.processed)) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [data, startPolling, stopPolling]);

  const [deleteFile] = useDeleteKnowledgeBaseFileMutation();
  const [createFolder] = useCreateKnowledgeBaseFolderMutation();

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [createFolderModal, openCreateFolderModal] = useFormModal<{
    name: string;
  }>({
    title: 'Ordner erstellen',
    okText: 'Erstellen',
    cancelText: 'Abbrechen',
    renderForm: (form, formProps) => (
      <Form form={form} layout="vertical" {...formProps}>
        <Form.Item name="name" label="Name" required>
          <Input />
        </Form.Item>
      </Form>
    ),
  });

  const handleOpenFolder = useCallback(
    (p?: string) => {
      return () => {
        setSearchParams({
          path: p ?? '',
        });
      };
    },
    [setSearchParams],
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      return async () => {
        if (
          await confirmModal({
            title: 'Löschen',
            type: 'warning',
            okText: 'Löschen',
            cancelText: 'Abbrechen',
            content: name,
          })
        ) {
          await deleteFile({
            variables: {
              id,
              knowledgeBaseId,
            },
            refetchQueries: ['knowledgeBaseDirectory'],
          });
        }
      };
    },
    [deleteFile, knowledgeBaseId],
  );

  const handleCreateFolder = useCallback(async () => {
    const values = await openCreateFolderModal();
    if (values) {
      await createFolder({
        variables: {
          name: values.name,
          path,
          knowledgeBaseId,
        },
        refetchQueries: ['knowledgeBaseDirectory'],
      });
    }
  }, [openCreateFolderModal, path, createFolder, knowledgeBaseId]);

  const columns: TableColumnsType<DataType> = useMemo(
    () => [
      {
        key: 'thumbnail',
        render: (_, record) => {
          if (record.type === 'folder')
            return <FontAwesomeIcon icon={faFolder} />;
          return record.processed === false ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              title="Die Datei wird verarbeitet..."
            />
          ) : (
            <FontAwesomeIcon icon={faFileCheck} />
          );
        },
        width: 20,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) =>
          record.type === 'folder' ? (
            <span
              className="cursor-pointer"
              onClick={handleOpenFolder(record.path)}
            >
              {text}
            </span>
          ) : (
            <a href={record.url ?? ''} target="_blank">
              {text}
            </a>
          ),
      },
      {
        title: 'Größe',
        dataIndex: 'size',
        key: 'size',
        render: (size) => {
          if (!size) return null;
          return size / 1024 < 1024
            ? `${(size / 1024).toFixed(1)} KB`
            : `${(size / 1024 / 1024).toFixed(1)} MB`;
        },
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        render: (actions, record) =>
          actions ?? (
            <div className="space-x-2">
              <Button
                type="text"
                onClick={handleDelete(record.id, record.name)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          ),
        width: 20,
      },
    ],
    [handleOpenFolder, handleDelete],
  );

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} wurde hochgeladen`);
    }

    if (!info.fileList.find((f) => f.status !== 'done')) {
      // All done
      refetch();
      setFileList([]);
    } else {
      setFileList(info.fileList);
    }
  };
  const beforeUpload = useCallback((file: RcFile) => {
    if (
      allowedMimeTypes &&
      !allowedMimeTypes.includes(file.type as UploadContentType)
    ) {
      message.error('Dieses Dateiformat wird leider nicht unterstützt!');
      return false;
    }

    if (file.size / 1024 / 1024 > maxUploadSizeMB) {
      message.error(`Dateien dürfen maximal ${maxUploadSizeMB} MB groß sein!`);
      return false;
    }

    return true;
  }, []);

  const uploadProps: UploadProps = {
    accept: allowedMimeTypes.join(','),
    beforeUpload,
    fileList,
    showUploadList: false,
    action: data?.knowledgeBaseDirectory.fileUploadInfo.url,
    onChange: handleUploadChange,
    multiple: true,
    data: (file) => {
      return {
        ...data?.knowledgeBaseDirectory.fileUploadInfo.fields,
        'Content-Type': file.type,
      };
    },
  };

  return (
    <Page>
      <div className="flex flex-row justify-between mb-2">
        <div>
          <PageHeader
            breadcrumbs={[
              {
                label: 'Wissensdatenbanken',
                link: '/companyAdmin/knowledgeBases',
              },
              {
                label: data?.knowledgeBase.name,
                link: '#',
              },
            ]}
          />
          <Breadcrumb
            items={[
              {
                title: data?.knowledgeBase.name ?? '',
                onClick: handleOpenFolder(),
              },
              ...(path
                ?.split('/')
                .filter((p) => `${p}`.trim() !== '')
                .map((p, index) => {
                  const subPath = path
                    ?.split('/')
                    .filter((_, i) => i <= index)
                    .join('/');

                  return {
                    title: p,
                    onClick: handleOpenFolder(`${subPath}/`),
                  };
                }) ?? []),
            ]}
            className="[&_.ant-breadcrumb-link]:cursor-pointer"
          />
        </div>
        <div className="flex flex-row space-x-10 items-center">
          <div className="space-x-2">
            <Button onClick={handleCreateFolder}>
              <FontAwesomeIcon icon={faFolderPlus} />
              Ordner erstellen
            </Button>
            <Upload {...uploadProps}>
              <Button>
                <FontAwesomeIcon icon={faUpload} />
                Hochladen
              </Button>
            </Upload>
          </div>
        </div>
      </div>

      <Upload.Dragger
        openFileDialogOnClick={false}
        {...uploadProps}
        className="[&_.ant-upload]:!p-0 [&>div]:!border-0"
      >
        <Table<DataType>
          pagination={false}
          loading={loading}
          columns={columns}
          dataSource={[
            ...fileList.map(
              (file) =>
                ({
                  id: file.uid,
                  key: file.uid,
                  name: (
                    <div className="space-x-2">
                      <Progress
                        type="circle"
                        trailColor={file.status}
                        percent={file.percent}
                        strokeWidth={20}
                        size={14}
                        format={(number) => `${number}%`}
                      />
                      <span>{file.name}</span>
                    </div>
                  ),
                  size: file.size,
                  type: 'file',
                  actions: '',
                  processed: false,
                }) satisfies DataType,
            ),
            ...(data?.knowledgeBaseDirectory.folders?.map(
              (folder) =>
                ({
                  id: folder.id,
                  key: folder.id,
                  path: folder.path,
                  name: folder.name,
                  size: 0,
                  type: 'folder',
                }) satisfies DataType,
            ) ?? []),
            ...(data?.knowledgeBaseDirectory.files?.map(
              (file) =>
                ({
                  id: file.id,
                  key: file.id,
                  name: file.fileName,
                  size: file.size,
                  url: file.url,
                  type: 'file',
                  processed: file.processed,
                }) satisfies DataType,
            ) ?? []),
          ]}
          rowHoverable
        />
      </Upload.Dragger>

      {createFolderModal}
    </Page>
  );
};

export default KnowledgeBase;
